import React, { useEffect, useRef, useState } from 'react';
import { generateDataAttr } from '../utils/generateDataAttr';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import { HeaderLayout } from './components/HeaderLayout';
import { FooterLayout } from './components/FooterLayout';
import { st, classes } from './MessageBoxFunctionalLayout.st.css';
import { dataHooks } from './MessageBoxFunctionalLayout.constants';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';
import { MessageBoxFunctionalLayoutProps } from './MessageBoxFunctionalLayout.types';

/** MessageBoxFunctionalLayout */
export const MessageBoxFunctionalLayout = ({
  theme = 'blue',
  buttonsHeight = 'small',
  disableCancel = false,
  disableConfirmation = false,
  noBodyPadding = false,
  fullscreen = false,
  withEmptyState = false,
  dataHook,
  title,
  onCancel,
  onOk,
  onClose,
  confirmText,
  confirmPrefixIcon,
  confirmSuffixIcon,
  cancelText,
  cancelPrefixIcon,
  cancelSuffixIcon,
  hideFooter,
  footerBottomChildren,
  closeButton,
  width,
  margin,
  sideActions,
  image,
  maxHeight,
  children,
}: MessageBoxFunctionalLayoutProps) => {
  const [hasScroll, setHasScroll] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [handleMessageBoxScroll] = useState(() =>
    throttle(() => {
      setScrolledToBottom(
        ref.current
          ? ref.current.scrollTop + ref.current.clientHeight ===
              ref.current.scrollHeight
          : false,
      );
    }, 16),
  );

  useEffect(() => {
    let scrollElement = ref.current;

    if (
      scrollElement &&
      scrollElement?.scrollHeight > scrollElement?.clientHeight
    ) {
      scrollElement.addEventListener('scroll', handleMessageBoxScroll);
      setHasScroll(true);
    }

    return () => {
      handleMessageBoxScroll.cancel();
      scrollElement?.removeEventListener('scroll', handleMessageBoxScroll);
    };
  }, [ref, handleMessageBoxScroll]);

  const Content = ({ newColorsBranding }: { newColorsBranding?: boolean }) => {
    const ContentLayout = () => (
      <div
        ref={ref}
        data-hook={dataHooks.content}
        className={st(classes.content, {
          scrollable: typeof maxHeight !== 'undefined',
          noPadding: noBodyPadding,
          newColorsBranding,
          fullscreenBody: fullscreen,
          noFooter: hideFooter,
          footerBorder: hasScroll && !scrolledToBottom,
          withEmptyState,
        })}
        style={{ maxHeight }}
      >
        {children}
      </div>
    );

    if (image && !withEmptyState && !newColorsBranding) {
      return (
        <div className={classes.contentWithImage}>
          <div className={st(classes.image)}>{image}</div>
          <ContentLayout />
        </div>
      );
    }

    return <ContentLayout />;
  };

  return (
    <WixDesignSystemContext.Consumer>
      {({ newColorsBranding }) => (
        <div
          data-hook={dataHook}
          className={st(classes.root, {
            fullscreen,
            newColorsBranding: newColorsBranding && !image,
            hasImage: newColorsBranding && !!image,
          })}
          style={{ width, margin }}
          {...generateDataAttr({ noBodyPadding, theme }, [
            'noBodyPadding',
            'theme',
          ])}
        >
          <div className={st(classes.topAreaContainer, { newColorsBranding })}>
            {newColorsBranding && image ? (
              <div className={classes.imageContainer}>{image}</div>
            ) : null}
            <div
              className={st(classes.contentAreaContainer, {
                newColorsBranding,
              })}
            >
              <HeaderLayout
                title={title}
                onCancel={onClose ? onClose : onCancel}
                theme={!newColorsBranding ? theme : 'newColorsBranding'}
                closeButton={closeButton}
                size={newColorsBranding ? 'large' : 'medium'}
                newColorsBranding={newColorsBranding}
              />
              <Content newColorsBranding={newColorsBranding} />
            </div>
          </div>
          {!hideFooter ? (
            <FooterLayout
              newColorsBranding={newColorsBranding}
              bottomChildren={footerBottomChildren}
              enableCancel={!disableCancel}
              enableOk={!disableConfirmation}
              buttonsHeight={buttonsHeight}
              confirmText={confirmText}
              confirmPrefixIcon={confirmPrefixIcon}
              confirmSuffixIcon={confirmSuffixIcon}
              cancelText={cancelText}
              cancelPrefixIcon={cancelPrefixIcon}
              cancelSuffixIcon={cancelSuffixIcon}
              onCancel={onCancel}
              onOk={onOk}
              theme={theme}
              sideActions={sideActions}
            />
          ) : null}
        </div>
      )}
    </WixDesignSystemContext.Consumer>
  );
};

MessageBoxFunctionalLayout.displayName = 'MessageBoxFunctionalLayout';

MessageBoxFunctionalLayout.propTypes = {
  dataHook: PropTypes.any,
  hideFooter: PropTypes.any,
  confirmText: PropTypes.any,
  confirmPrefixIcon: PropTypes.any,
  confirmSuffixIcon: PropTypes.any,
  cancelText: PropTypes.any,
  cancelPrefixIcon: PropTypes.any,
  cancelSuffixIcon: PropTypes.any,
  theme: PropTypes.any,
  onOk: PropTypes.any,
  onCancel: PropTypes.any,
  onClose: PropTypes.any,
  width: PropTypes.any,
  margin: PropTypes.any,
  title: PropTypes.any,
  children: PropTypes.any,
  maxHeight: PropTypes.any,
  buttonsHeight: PropTypes.any,
  closeButton: PropTypes.any,
  disableCancel: PropTypes.any,
  disableConfirmation: PropTypes.any,
  noBodyPadding: PropTypes.any,
  footerBottomChildren: PropTypes.any,
  fullscreen: PropTypes.any,
  withEmptyState: PropTypes.any,
  sideActions: PropTypes.any,
  image: PropTypes.any,
};
